import { useEffect, useRef, useState } from 'react';
import { UserAvatar } from '../userAvatar/UserAvatar';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { deleteComment, getPostWithComments } from '../../features/sessions/sessionPostProject/sessionPostProject';
import { CircularProgress } from '@mui/material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { Box, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

export default function PostCommentsInPost({ groupId, postId }: { groupId: string; postId: string }) {
    const authHeaders             = useSelector((state: RootState) => state.session.authHeaders);
    const currentUser             = useSelector((state: RootState) => state.session.currentUser);
    const postCommentsList        = useSelector((state: RootState) => state.sessionProjectPosts.postComments);
    const loadingMorePostComments = useSelector((state: RootState) => state.sessionProjectPosts.loadingMorePostComments);
    const [postCommentsPage, setPostCommentsPage] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);
    const dispatch     = useDispatch<AppDispatch>();

    useEffect(() => {
        async function showPostWithComments() {
            if (authHeaders && currentUser?.nickname && groupId && postId) {
                await dispatch(
                    getPostWithComments({
                        authHeaders: authHeaders,
                        routeParams: {
                            userNickname: currentUser.nickname,
                            groupId: groupId,
                            postId: postId,
                            postCommentsPage: `${postCommentsPage}`,
                        },
                    })
                );
            }
        }

        showPostWithComments();
    }, [postCommentsPage]);

    function handleFormatDate(date: any) {
        return format(new Date(date), 'dd MMM yyyy HH:mm');
    }

    function handleRelativeTime(date: any) {
        const originalDate = new Date(date);
        const now = new Date();
        const diffMs = Math.abs(now.getTime() - originalDate.getTime());

        const diffMinutes = Math.floor(diffMs / (1000 * 60));
        const diffHours   = Math.floor(diffMinutes / 60);
        const diffDays    = Math.floor(diffHours / 24);
        const diffMonths  = Math.floor(diffDays / 30);
        const diffYears   = Math.floor(diffMonths / 12);

        if (diffYears > 0)   return `${diffYears} year${diffYears > 1 ? 's' : ''}`;
        if (diffMonths > 0)  return `${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
        if (diffDays > 0)    return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
        if (diffHours > 0)   return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
        if (diffMinutes > 0) return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
        return 'just now';
    }

    useEffect(() => {
        const handleScroll = () => {
            const container = document.getElementById('container-ul-post-comments');
            if (!container) return;

            const { scrollTop, clientHeight, scrollHeight } = container;
            if (scrollTop + clientHeight >= scrollHeight && !loadingMorePostComments) {
                setPostCommentsPage(postCommentsPage + 1);
            }
        };

        setTimeout(() => {
            const container = document.getElementById('container-ul-post-comments');
            if (container) {
                container.addEventListener('scroll', handleScroll);
                return () => container.removeEventListener('scroll', handleScroll);
            }
        }, 100);
    }, [loadingMorePostComments]);

    async function handleDeleteComment(postCommentId: number, postId: number) {
        if (authHeaders && currentUser?.nickname) {
            const response = await dispatch(
                deleteComment({
                    authHeaders: authHeaders,
                    routeParams: {
                        userNickname: currentUser.nickname,
                        postCommentId: postCommentId,
                        postId: postId,
                    },
                })
            );

            if (
                response.payload &&
                response.payload.errors &&
                response.payload.errors.length > 0
            ) {
                response.payload.errors.map((item: string) => { toast.error(item); });
            }
        }
    }

    return (
        <Box
            id='container-ul-post-comments'
            ref={containerRef}
            sx={{
                maxHeight: '280px',
                overflowY: 'auto',
                mb: 1.5,
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255,255,255,0.15) transparent',
            }}
        >
            {postCommentsList.length === 0 && !loadingMorePostComments ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        py: 2,
                    }}
                >
                    <Typography variant='body2' color={TEXT_SECONDARY}>
                        No comments yet
                    </Typography>
                </Box>
            ) : (
                <Box>
                    {postCommentsList.map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                bgcolor: BG_CARD,
                                borderRadius: '8px',
                                border: BORDER_SUBTLE,
                                mb: 1,
                                px: 1.5,
                                py: 1,
                            }}
                        >
                            {/* Cabeçalho do comentário */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 0.75,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <UserAvatar src={item.user.userMoreInfo?.avatarUrl} size={28} />
                                    <Box>
                                        <Typography
                                            variant='caption'
                                            fontWeight={700}
                                            color={TEXT_PRIMARY}
                                            sx={{ display: 'block', lineHeight: 1.2 }}
                                        >
                                            {item.user.nickname}
                                        </Typography>
                                        <Typography variant='caption' color={TEXT_SECONDARY} sx={{ fontSize: '0.65rem' }}>
                                            {handleFormatDate(item.createdAt)}
                                            {' '}
                                            <Box component='span' sx={{ opacity: 0.7 }}>
                                                ({handleRelativeTime(item.createdAt)})
                                            </Box>
                                        </Typography>
                                    </Box>
                                </Box>

                                {item.user.id === currentUser?.id && (
                                    <IconButton
                                        size='small'
                                        onClick={() => handleDeleteComment(item.id, item.postId)}
                                        sx={{
                                            color: TEXT_SECONDARY,
                                            '&:hover': { color: '#e57373', bgcolor: 'rgba(192,57,43,0.1)' },
                                        }}
                                    >
                                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                )}
                            </Box>

                            {/* Corpo do comentário */}
                            <Typography
                                variant='body2'
                                color={TEXT_PRIMARY}
                                sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    fontSize: { xs: '0.775rem', sm: '0.825rem' },
                                    pl: 4.5,
                                }}
                            >
                                {item.body}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {loadingMorePostComments && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={20} sx={{ color: BRAND_RED }} />
                </Box>
            )}
        </Box>
    );
}
